// =============================================================
//  Topbar-notificatiebel — in-app inbox voor de ingelogde gebruiker
// -------------------------------------------------------------
//  - Server-gerenderde lijst (ongelezen, nieuwste eerst) in een
//    Bootstrap-dropdown in de app-chrome (_app_chrome.html.erb)
//  - Wegklikken (mark read) hergebruikt de /me-endpoints — die zijn
//    user-scoped en rolvrij, dus planner én agent kunnen erbij
//  - "Enable push" in de voet: zelfde subscribe-kern als de
//    /me-opt-in (lib/webpush.js), zodat ook planners die nooit op
//    /me komen push kunnen aanzetten
//  - Bij al-verleende permissie her-subscribet dit stil, één keer
//    per sessie (sessionStorage-vlag) — houdt endpoints vers na
//    browserrotatie/sleutelwissel, ook voor planners
//  Geladen via de application-bundle; delegated handlers, dus
//  Turbo-bestendig.
// =============================================================

import { on } from '../lib/dom';
import { pushSupported, ensurePushSubscription, isPushOptedOut, setPushOptOut } from '../lib/webpush';

function csrf () {
  const el = document.querySelector('meta[name="csrf-token"]');
  return el ? el.content : '';
}

function markRead (url, method, keepalive) {
  return fetch(url, {
    method: method,
    headers: { 'X-CSRF-Token': csrf(), Accept: 'application/json' },
    keepalive: !!keepalive
  }).catch(function () {});
}

function bellBadge () { return document.getElementById('notif-bell-badge'); }

function decrementBadge () {
  const b = bellBadge();
  if (!b) return;
  const n = Math.max(0, (parseInt(b.textContent, 10) || 0) - 1);
  b.textContent = n;
  b.hidden = n === 0;
}

function showEmptyIfDone () {
  const list = document.getElementById('notif-bell-list');
  if (!list) return;
  if (list.querySelector('.notif-bell__item')) return;
  // De lijst toont max. 8 items maar de badge telt ALLE ongelezen: pas als ook
  // de badge op 0 staat is de gebruiker echt bij; anders blijft "Mark all as
  // read" de uitweg voor de rest (server-side clear + badge naar 0).
  const b = bellBadge();
  const remaining = (b && !b.hidden) ? (parseInt(b.textContent, 10) || 0) : 0;
  const empty = list.querySelector('.notif-bell__empty');
  if (empty) empty.hidden = remaining > 0;
  const all = document.getElementById('notif-bell-readall');
  if (all) all.hidden = remaining === 0;
}

// Eén kaartje wegklikken.
on('click', '.notif-bell__dismiss', function () {
  const item = this.closest('.notif-bell__item');
  if (!item) return;
  const id = item.dataset.id;
  if (id) markRead('/me/notifications/' + encodeURIComponent(id) + '/read', 'PATCH');
  item.remove();
  decrementBadge();
  showEmptyIfDone();
});

// Alles gelezen.
on('click', '#notif-bell-readall', function () {
  markRead('/me/notifications/read_all', 'POST');
  document.querySelectorAll('#notif-bell-list .notif-bell__item').forEach(function (el) { el.remove(); });
  const b = bellBadge();
  if (b) { b.textContent = '0'; b.hidden = true; }
  showEmptyIfDone();
});

// Doorklikken op een notificatie = gelezen (keepalive zodat de PATCH de
// navigatie overleeft); de pagina laadt daarna toch opnieuw.
on('click', '.notif-bell__link', function () {
  const item = this.closest('.notif-bell__item');
  if (item && item.dataset.id) {
    markRead('/me/notifications/' + encodeURIComponent(item.dataset.id) + '/read', 'PATCH', true);
  }
});

// Push aanzetten vanuit de bel (voor planners dé plek — zij komen niet op /me).
on('click', '#notif-bell-push', function () {
  const bell = document.getElementById('notif-bell');
  const btn = this;
  if (!bell || !bell.dataset.vapidKey) return;
  setPushOptOut(bell.dataset.userId, false); // expliciet aan wint van eerdere opt-out
  ensurePushSubscription(bell.dataset.vapidKey)
    .then(function (r) {
      if (r && r.ok) {
        btn.hidden = true;
        const off = document.getElementById('notif-bell-push-off');
        if (off) off.hidden = false;
      }
    })
    .catch(function () { /* geweigerd of pushdienst onbereikbaar — knop blijft */ });
});

// Push voor dit toestel uitzetten: browser-unsubscribe + server-rij weg.
// Permissie blijft 'granted', dus opnieuw aanzetten kan zonder prompt.
on('click', '#notif-bell-push-off', function () {
  const btn = this;
  const bell = document.getElementById('notif-bell');
  navigator.serviceWorker.getRegistration()
    .then(function (reg) { return reg && reg.pushManager.getSubscription(); })
    .then(function (sub) {
      if (!sub) return null;
      const endpoint = sub.endpoint;
      return sub.unsubscribe().then(function () {
        const token = (document.querySelector('meta[name="csrf-token"]') || {}).content;
        return fetch('/me/push_subscription', {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': token, 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ endpoint: endpoint })
        });
      });
    })
    .then(function () {
      // Persistent: anders zet de stille her-registratie (nieuwe tab/sessie)
      // de bewust uitgezette push gewoon weer aan.
      if (bell) setPushOptOut(bell.dataset.userId, true);
      btn.hidden = true;
      const onBtn = document.getElementById('notif-bell-push');
      if (onBtn) onBtn.hidden = false;
    })
    .catch(function () { /* pushdienst onbereikbaar — knop blijft */ });
});

function bellInit () {
  const bell = document.getElementById('notif-bell');
  if (!bell || !pushSupported() || !bell.dataset.vapidKey) return;
  const btn = document.getElementById('notif-bell-push');
  if (Notification.permission === 'default' && btn) btn.hidden = false;
  // Per gebruiker gevlagd: na een account-wissel in dezelfde tab moet het
  // toestel opnieuw op de níéuwe user geregistreerd worden (de endpoint-rij
  // wordt server-side geclaimd door wie er nu is ingelogd).
  const optedOut = isPushOptedOut(bell.dataset.userId);
  const flagKey = 'aimPushSynced:' + (bell.dataset.userId || '');
  if (Notification.permission === 'granted' && !optedOut && !sessionStorage.getItem(flagKey)) {
    // Stille refresh, hooguit één keer per sessie — geen POST op elke pagina.
    // NIET bij een expliciete opt-out: die moet tabs/sessies overleven.
    sessionStorage.setItem(flagKey, '1');
    ensurePushSubscription(bell.dataset.vapidKey).catch(function () {});
  }
  if (Notification.permission === 'granted') {
    if (optedOut) {
      // Permissie staat nog, maar de gebruiker zette push bewust uit —
      // toon de aanzet-knop als heraanmeld-ingang.
      if (btn) btn.hidden = false;
    } else {
      // Uitknop tonen wanneer dit toestel daadwerkelijk geabonneerd is.
      navigator.serviceWorker.getRegistration()
        .then(function (reg) { return reg && reg.pushManager.getSubscription(); })
        .then(function (sub) {
          const off = document.getElementById('notif-bell-push-off');
          if (off && sub) off.hidden = false;
        })
        .catch(function () {});
    }
  }
}

// /me toont dezelfde notificaties ook als kaartenlijst; me.js meldt dismissals
// via dit event zodat bel en lijst niet uit de pas lopen.
document.addEventListener('aim:notif-read', function (e) {
  const id = e.detail && e.detail.id;
  if (id === 'all') {
    document.querySelectorAll('#notif-bell-list .notif-bell__item').forEach(function (el) { el.remove(); });
    const b = bellBadge();
    if (b) { b.textContent = '0'; b.hidden = true; }
  } else if (id) {
    const item = document.querySelector('#notif-bell-list .notif-bell__item[data-id="' + CSS.escape(String(id)) + '"]');
    if (item) item.remove();
    decrementBadge();
  }
  showEmptyIfDone();
});

// Turbolinks snapshot een pagina inclusief open dropdown — sluit de bel vóór
// het cachen, anders staat hij na Back stale open.
document.addEventListener('turbo:before-cache', function () {
  const btn = document.getElementById('notif-bell-btn');
  if (btn && window.bootstrap) {
    const dd = window.bootstrap.Dropdown.getInstance(btn);
    if (dd) dd.hide();
  }
});

document.addEventListener('turbo:load', bellInit);
bellInit();
